<template>
  <AppWithLoading :fetch="fetchCareerPortalData" v-slot:default="{ loader, localLoading }">
    <VRow>
      <VCol cols="12">
        <VTabs
          v-model="tab"
          background-color="transparent"
          color="dark"
          slider-size="4"
          slider-color="primary"
        >
          <VTab class="tab-name mr-4 mr-lg-6">
            {{ $t('start') }}
          </VTab>
          <VTab class="tab-name mr-4 mr-lg-6">
            {{ $t('design') }}
          </VTab>
          <VTab class="tab-name mr-4 mr-lg-6">
            {{ $t('blog') }}
          </VTab>
          <VTab class="tab-name mr-4 mr-lg-6">
            {{ $t('settings') }}
          </VTab>
        </VTabs>

        <VTabsItems v-model="tab">
          <VTabItem>
            <Start
              v-if="!localLoading"
              :loader="loader"
              :local-loading="localLoading"
              :company="company"
              :account="account"
              :articles="articles"
              :domain="domain"
            />
          </VTabItem>
          <VTabItem>
            <CareerPortalForm
              v-if="!localLoading"
              :loader="loader"
              :local-loading="localLoading"
              :company="company"
              :account="account"
              class="pb-12"
            />
          </VTabItem>
          <VTabItem>
            <Blog
              v-if="!localLoading"
              :loader="loader"
              :local-loading="localLoading"
              :account="account"
            />
          </VTabItem>
          <VTabItem>
            <Domain />
          </VTabItem>
        </VTabsItems>
      </VCol>
    </VRow>
  </AppWithLoading>
</template>

<script>
import CareerPortalForm from '@/components/Company/CareerPortalForm';
import { ref, toRefs, computed } from '@vue/composition-api';
import Start from '@/components/Company/Start.vue';
import Blog from '@/components/Company/Blog';
import Domain from '@/components/Company/Domain.vue';
import AppWithLoading from '@/components/AppWithLoading';
import useCompany from '@/compositions/useCompany';
import useArticle from '@/compositions/useArticle';
import { portalTabs } from '@/enums';
import useAccount from '@/compositions/accounting/useAccount';
import useDomain from '../compositions/useDomain';

export default {
  name: 'CareerPortal',
  metaInfo() {
    return {
      title: this.$t('careerPortal'),
    };
  },
  components: {
    AppWithLoading,
    Start,
    Blog,
    Domain,
    CareerPortalForm,
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      default: null,
    },
  },

  setup(props, { root }) {
    const { company } = toRefs(props);
    const domain = ref('');

    const { getTemplates } = useCompany();
    const { articles, fetchAll } = useArticle();
    const { account, fetch } = useAccount();
    const { getDomain } = useDomain();

    async function fetchCareerPortalData() {
      await getTemplates();
      await fetchAll(company.value.id);
      await fetch();
      domain.value = await getDomain();
    }

    const { type, addType } = toRefs(props);

    const tabList = ref([portalTabs.START, portalTabs.DESIGN, portalTabs.ARTICLES, portalTabs.SETTINGS]);

    const tab = computed({
      get() {
        return tabList.value.indexOf(type.value) || 0;
      },
      set(index) {
        if (index === 1) {
          root.$router.replace({
            name: 'career-portal',
            params: { addType: portalTabs.PORTAL, type: tabList.value[index] },
          });
        } else {
          root.$router.replace({ name: 'career-portal', params: { type: tabList.value[index] } });
        }
      },
    });

    function changeAddType(payload) {
      if (payload !== addType.value && payload && type.value === portalTabs.SETTINGS) {
        root.$router.replace({
          name: 'career-portal',
          params: { addType: payload, type: type.value },
        });
      }
    }

    return {
      tabList,
      tab,
      portalTabs,
      changeAddType,
      fetchCareerPortalData,
      account,
      articles,
      domain,
    };
  },
};
</script>

<style lang="scss" scoped>
.tab-name {
  font-size: 1.25rem;
  letter-spacing: 0.04em;
}

@media #{map-get($display-breakpoints, 'md-and-down')} {
  .tab-name {
    font-size: 1.125rem;
  }
}
</style>

<i18n locale="en" lang="yaml">
  start: Start
  design: Design
  blog: Blog
  settings: Settings
  careerPortal: Portal
</i18n>

<i18n locale="ru" lang="yaml">
  start: Старт
  design: Дизайн
  blog: Блог
  settings: Настройки
  careerPortal: Портал
</i18n>
